import React, { MouseEvent, useState, FC } from "react";

import { QueryObserverResult } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  useBoolean,
  useClipboard,
  useApiMembersEmployeeConditionSubmissionsSubmissionRepliesDestroy,
  useApiMembersEmployeeConditionSubmissionsSubmissionRepliesUpdate,
  useEditorState,
  useEditorStateLinkContentGetter,
} from "~/hooks";

import {
  EmployeeConditionSubmissionReplyDetail,
  CommentEditForm,
} from "~/components/organisms";

import {
  EmployeeConditionSubmissionReplyType,
  PostDetailMenuItemType,
  AvatarAndNameEmployeeType,
  PreviewWithFileType,
  ApiMembersEmployeeConditionSubmissionsSubmissionRepliesIndexResponseType,
} from "~/domains";

import { Href } from "~/constants/postUrl";

type PropsType = {
  isHighlight?: boolean;
  employeeConditionSubmissionReply: EmployeeConditionSubmissionReplyType;
  mentions: AvatarAndNameEmployeeType[];
  refetch: () => Promise<
    QueryObserverResult<
      ApiMembersEmployeeConditionSubmissionsSubmissionRepliesIndexResponseType,
      unknown
    >
  >;
};

export const EmployeeConditionSubmissionReplyDetailWithEditForm: FC<
  PropsType
> = ({
  isHighlight = false,
  employeeConditionSubmissionReply,
  refetch,
  mentions,
}: PropsType) => {
  const { clipboardToCopy } = useClipboard();
  const {
    isChecked: isEditFormOpen,
    setFalse: closeEditForm,
    toggle: toggleEditForm,
  } = useBoolean(false);

  const [editorState] = useEditorState(
    employeeConditionSubmissionReply.content,
  );
  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const { fetchedData } = useEditorStateLinkContentGetter({
    watchEditorState: editorState.value,
    isWatch: isEditFormOpen,
  });

  const { mutate: updateRequest, isLoading } =
    useApiMembersEmployeeConditionSubmissionsSubmissionRepliesUpdate();

  const { mutate: deleteRequest } =
    useApiMembersEmployeeConditionSubmissionsSubmissionRepliesDestroy();

  const deleteEmployeeConditionSubmissionReplyRequest = () => {
    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;

    deleteRequest(
      {
        employeeConditionSubmissionId:
          employeeConditionSubmissionReply.employeeConditionSubmissionId,
        id: employeeConditionSubmissionReply.id,
      },
      {
        onSuccess: async (data) => {
          toast(data.message);
          await refetch();
        },
      },
    );
  };

  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const body = {
      content: editorState.jsonContent,
      attachFiles: files,
    };
    updateRequest(
      {
        employeeConditionSubmissionId:
          employeeConditionSubmissionReply.employeeConditionSubmissionId,
        id: employeeConditionSubmissionReply.id,
        body,
      },
      {
        onSuccess: async (data) => {
          toast(data.message);
          await refetch().then(() => {
            closeEditForm();
          });
        },
      },
    );
  };

  const detailItems: PostDetailMenuItemType[] = [
    {
      icon: "ioCopyOutline",
      text: "リンクをコピー",
      onClick: () =>
        clipboardToCopy(
          `${location.origin}${Href["EmployeeCondition::SubmissionReply"](
            employeeConditionSubmissionReply.id,
          )}`,
        ),
      disabled: false,
    },
    {
      icon: "ioCreateOutline",
      text: "編集する",
      onClick: () => toggleEditForm(),
      disabled: !employeeConditionSubmissionReply.editable,
    },
    {
      icon: "ioTrashOutline",
      text: "削除する",
      onClick: () => deleteEmployeeConditionSubmissionReplyRequest(),
      disabled: !employeeConditionSubmissionReply.editable,
    },
  ];

  return (
    <div className="" id={employeeConditionSubmissionReply.id}>
      {isEditFormOpen ? (
        <CommentEditForm
          submitButton={{
            isSubmitting: isLoading,
            onSubmit: handleSubmit,
          }}
          mentions={mentions}
          post={{
            createdAt: employeeConditionSubmissionReply.createdAt,
            employee: employeeConditionSubmissionReply.employee,
            content: employeeConditionSubmissionReply.content,
          }}
          closeEditForm={closeEditForm}
          files={files}
          setFiles={setFiles}
          editorStateContent={editorState.value}
          setEditorStateContent={editorState.onChange}
          blockQuote={fetchedData}
        />
      ) : (
        <EmployeeConditionSubmissionReplyDetail
          employeeConditionSubmissionReply={employeeConditionSubmissionReply}
          menuItems={detailItems}
          isHighlight={isHighlight}
        />
      )}
    </div>
  );
};
